import { useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { SendMessageParams } from '../types';
import { uploadChatFile, getFileTypeFromUri, getMessageTypeFromFileType } from '../utils/fileUpload';
import * as ImagePicker from 'expo-image-picker';

/**
 * Hook for chat actions: send, edit, delete messages
 */
export const useChatActions = (chatId: string, userId: string) => {
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);

  /**
   * Send a text message
   */
  const sendMessage = async (params: SendMessageParams) => {
    if (!userId) return null;

    try {
      setSending(true);

      const { data, error } = await supabase
        .from('messages')
        .insert({
          chat_id: params.chat_id,
          sender_id: userId,
          content: params.content,
          type: params.type || 'text',
          file_url: params.file_url,
          file_name: params.file_name,
          file_type: params.file_type,
          file_size: params.file_size,
          reply_to_id: params.reply_to_id,
          mentioned_user_ids: params.mentioned_user_ids || [],
        })
        .select(`
          *,
          sender:profiles!sender_id(id, full_name, nickname, avatar_url),
          reactions:message_reactions(
            id, emoji, user_id,
            user:profiles(id, full_name, avatar_url)
          )
        `)
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    } finally {
      setSending(false);
    }
  };

  /**
   * Send an image message
   */
  const sendImage = async () => {
    try {
      setUploading(true);

      // Request permissions
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        throw new Error('Permission to access media library denied');
      }

      // Pick image - using new MediaType array instead of deprecated MediaTypeOptions
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        allowsEditing: false, // Disable cropping interface
        quality: 0.8,
      });

      if (result.canceled) return null;

      const asset = result.assets[0];
      const fileType = getFileTypeFromUri(asset.uri);

      // Upload to both buckets
      const uploadResult = await uploadChatFile(
        asset.uri,
        chatId,
        userId,
        fileType
      );

      // Send message with image
      return await sendMessage({
        chat_id: chatId,
        type: 'image',
        file_url: uploadResult.chatFileUrl,
        file_name: uploadResult.fileName,
        file_type: uploadResult.fileType,
        file_size: uploadResult.fileSize,
      });
    } catch (error) {
      console.error('Error sending image:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  /**
   * Send a file (document, video, etc.)
   */
  const sendFile = async () => {
    try {
      setUploading(true);

      // Pick video from library - using new MediaType array instead of deprecated MediaTypeOptions
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['videos'],
        allowsEditing: false,
        quality: 1,
      });

      if (result.canceled) return null;

      const asset = result.assets[0];
      const fileType = getFileTypeFromUri(asset.uri);
      const messageType = getMessageTypeFromFileType(fileType);

      // Upload to both buckets
      const uploadResult = await uploadChatFile(
        asset.uri,
        chatId,
        userId,
        fileType
      );

      // Send message with file
      return await sendMessage({
        chat_id: chatId,
        type: messageType,
        file_url: uploadResult.chatFileUrl,
        file_name: uploadResult.fileName,
        file_type: uploadResult.fileType,
        file_size: uploadResult.fileSize,
      });
    } catch (error) {
      console.error('Error sending file:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  /**
   * Edit a message
   */
  const editMessage = async (messageId: string, newContent: string) => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .update({
          content: newContent,
          is_edited: true,
          edited_at: new Date().toISOString(),
        })
        .eq('id', messageId)
        .eq('sender_id', userId)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error editing message:', error);
      throw error;
    }
  };

  /**
   * Delete message for everyone
   */
  const deleteMessage = async (messageId: string) => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .update({
          content: null,
          is_deleted: true,
        })
        .eq('id', messageId)
        .eq('sender_id', userId)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  };

  /**
   * React to a message with emoji
   */
  const reactToMessage = async (messageId: string, emoji: string) => {
    try {
      // Check if reaction already exists
      const { data: existing } = await supabase
        .from('message_reactions')
        .select('*')
        .eq('message_id', messageId)
        .eq('user_id', userId)
        .eq('emoji', emoji)
        .single();

      if (existing) {
        // Remove reaction
        await supabase
          .from('message_reactions')
          .delete()
          .eq('id', existing.id);
      } else {
        // Add reaction
        await supabase
          .from('message_reactions')
          .insert({
            message_id: messageId,
            user_id: userId,
            emoji,
          });
      }
    } catch (error) {
      console.error('Error reacting to message:', error);
      throw error;
    }
  };

  return {
    sendMessage,
    sendImage,
    sendFile,
    editMessage,
    deleteMessage,
    reactToMessage,
    sending,
    uploading,
  };
};

